'use strict'

// Live-app addresses that another site can place in an iframe.
// `embed` is reserved as the first segment after the application.
// A page or view on that address uses the same login as the live app.

const { isViewVisibleToUser } = require('./view-visibility')

const EMBED_MODES = {
  app: true,
  page: true,
  view: true
}

const EMBED_VIEW_TYPES = {
  table: true,
  cards: true,
  card: true,
  calendar: true,
  maps: true,
  carousel: true,
  pivot: true,
  chart: true,
  form: true,
  login: true,
  registration: true,
  menu: true,
  richtext: true,
  staticimage: true,
  map: true,
  custom: true
}

const TOP_LEVEL_TYPES = {
  table: true,
  pivot: true,
  custom: true,
  form: true,
  login: true,
  registration: true,
  row: true,
  column: true,
  chart: true,
  richtext: true,
  staticimage: true
}

function decodeSegment (segment) {

  try {

    return decodeURIComponent(segment)
  } catch (err) {

    return segment
  }
}

function normalFromRest (rest) {

  const list = rest || []

  if (!list.length) return { pageUniqueTitle: null, accessKey: null, segments: [] }

  if (list.length === 1) {

    return { pageUniqueTitle: list[0], accessKey: null, segments: list.slice() }
  }

  return {
    pageUniqueTitle: list[list.length - 2],
    accessKey: list[list.length - 1],
    segments: list.slice()
  }
}

function parseLiveSegments (segments) {

  const list = Array.isArray(segments) ? segments.slice() : []

  if (list[0] !== 'embed') {

    return Object.assign({ embed: null }, normalFromRest(list))
  }

  const marker = list[1]

  if (marker === 'page') {

    return Object.assign({
      embed: { mode: 'page', viewUniqueTitle: null }
    }, normalFromRest(list.slice(2)))
  }

  if (marker === 'view') {

    const pageUniqueTitle = list[2] || null
    const viewUniqueTitle = list[3] || null
    const accessKey = list.length > 4 ? list[4] : null
    const trail = []

    if (pageUniqueTitle) trail.push(pageUniqueTitle)
    if (accessKey) trail.push(accessKey)

    return {
      embed: { mode: 'view', viewUniqueTitle },
      pageUniqueTitle,
      accessKey,
      segments: trail
    }
  }

  const rest = marker === 'app' || !marker ? list.slice(2) : list.slice(1)

  return Object.assign({
    embed: { mode: 'app', viewUniqueTitle: null }
  }, normalFromRest(rest))
}

function parseAppPath (path) {

  const raw = String(path || '').split('?')[0].split('#')[0]
  const trimmed = raw.length > 1 ? raw.replace(/\/+$/, '') : raw
  const segments = trimmed.split('/').filter(Boolean).map(decodeSegment)

  if (!segments.length) return null

  return Object.assign({ application: segments[0] }, parseLiveSegments(segments.slice(1)))
}

function embedAppPath ({ application, mode, segments, pageUniqueTitle, viewUniqueTitle, accessKey }) {

  if (!application || !EMBED_MODES[mode]) return ''

  const parts = [application, 'embed', mode]

  if (mode === 'view') {

    if (pageUniqueTitle) parts.push(pageUniqueTitle)
    if (viewUniqueTitle) parts.push(viewUniqueTitle)
    if (accessKey) parts.push(accessKey)
  } else if (segments && segments.length) {

    for (const segment of segments) parts.push(segment)
  } else if (pageUniqueTitle) {

    parts.push(pageUniqueTitle)
    if (accessKey) parts.push(accessKey)
  }

  return `/${parts.map((part) => encodeURIComponent(part)).join('/')}`
}

function pageRequiresLoginFlag (page) {

  return !!(page && (page.requiresLogin === true || page.requiresLogin === 'true'))
}

function collectEmbedViews (components, page, views) {

  for (const component of components || []) {

    if (!component || typeof component !== 'object') continue

    if (EMBED_VIEW_TYPES[component.type] && component.uniqueTitle) {

      views.push({
        pageUniqueTitle: page.uniqueTitle,
        pageTitle: page.title || page.uniqueTitle,
        uniqueTitle: component.uniqueTitle,
        title: component.title || component.uniqueTitle,
        type: component.type,
        requiresLogin: !!(component.requiresLogin === true || component.requiresLogin === 'true')
      })
    }

    collectEmbedViews(component.components, page, views)
    collectEmbedViews(component.children, page, views)
  }
}

function embedTargets (pages) {

  const pageChoices = []
  const viewChoices = []

  for (const page of pages || []) {

    if (!page || !page.uniqueTitle) continue

    pageChoices.push({
      uniqueTitle: page.uniqueTitle,
      title: page.title || page.uniqueTitle,
      requiresLogin: pageRequiresLoginFlag(page)
    })
    collectEmbedViews(page.components, page, viewChoices)
  }

  return { pages: pageChoices, views: viewChoices }
}

function embedPathTail ({ mode, pageUniqueTitle, viewUniqueTitle }) {

  if (!EMBED_MODES[mode]) return ''

  if (mode === 'page' && !pageUniqueTitle) return ''
  if (mode === 'view' && (!pageUniqueTitle || !viewUniqueTitle)) return ''

  const parts = ['embed', mode]

  if (mode === 'page' || mode === 'view') parts.push(pageUniqueTitle)
  if (mode === 'view') parts.push(viewUniqueTitle)

  return parts.join('/')
}

function stayInsideEmbed (fromPath, toPath) {

  const from = parseAppPath(fromPath)
  const to = parseAppPath(toPath)

  if (!from || !from.embed || !to) return ''
  if (from.application !== to.application) return ''
  if (to.embed) return ''
  if (to.pageUniqueTitle === '__preview__') return ''

  const mode = from.embed.mode === 'app' ? 'app' : 'page'

  return embedAppPath({
    application: to.application,
    mode,
    segments: to.segments
  })
}

function escapeAttribute (value) {

  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function embedSnippet ({ src, title }) {

  const address = String(src || '').trim()

  if (!address) return ''

  const name = String(title || 'App').trim() || 'App'

  return `<iframe src="${escapeAttribute(address)}" title="${escapeAttribute(name)}" style="width: 100%; height: 600px; border: 0;"></iframe>`
}

function walkComponents (components, visit) {

  for (const component of components || []) {

    if (!component || typeof component !== 'object') continue
    if (visit(component)) return component

    const nested = walkComponents(component.components, visit) || walkComponents(component.children, visit)

    if (nested) return nested
  }

  return null
}

function findEmbedComponent (components, uniqueTitle) {

  if (!uniqueTitle) return null

  return walkComponents(components, (component) => component.uniqueTitle === uniqueTitle)
}

function findEmbedLogin (components) {

  return walkComponents(components, (component) => {

    return component.type === 'login' && isViewVisibleToUser(component, false)
  })
}

function componentForEmbed (component) {

  if (!component) return null
  if (TOP_LEVEL_TYPES[component.type]) return component

  return {
    type: 'column',
    uniqueTitle: `embed_${component.uniqueTitle || 'view'}`,
    components: [component]
  }
}

function embedViewPresentation (components, viewUniqueTitle, loggedIn) {

  const view = findEmbedComponent(components, viewUniqueTitle)

  if (!view) return { missing: true }

  if (isViewVisibleToUser(view, !!loggedIn)) {

    return { component: componentForEmbed(view) }
  }

  if (view.requiresLogin && !loggedIn) {

    const login = findEmbedLogin(components)
    const sameView = login && login.uniqueTitle === view.uniqueTitle

    return {
      loginRequired: true,
      loginComponent: sameView ? null : componentForEmbed(login)
    }
  }

  return { hidden: true }
}

function embedFrameHeaders (urlPath) {

  const path = String(urlPath || '').split('?')[0]

  if (path.startsWith('/dist/') || path.startsWith('/vendor/') || /\.[a-z0-9]+$/i.test(path)) {

    return {}
  }

  return { 'Content-Security-Policy': 'frame-ancestors *' }
}

module.exports = {
  parseLiveSegments,
  parseAppPath,
  embedAppPath,
  embedPathTail,
  embedTargets,
  stayInsideEmbed,
  embedSnippet,
  findEmbedComponent,
  embedViewPresentation,
  embedFrameHeaders
}
